// Payment status constants
export const PaymentStatus = {
  UNPAID: 'unpaid',
  EXPIRED: 'expired',

  STARTER_TRIAL: 'starterTrial',
  STARTUP_TRIAL: 'startupTrial',
  PRO_TRIAL: 'proTrial',

  STARTER: 'starter',
  STARTUP: 'startup',
  PRO: 'pro',
} as const;

export type PaymentStatusType = (typeof PaymentStatus)[keyof typeof PaymentStatus];

// Interface for user signup payload
export interface SignupPayload {
  email: string;
  organisation: string;
  password: string;
  firstName: string;
  lastName: string;
  status: string;
  paymentStatus: PaymentStatusType;
}

// Interface for signup success response
export interface SignupResponse {
  id: string;
  sub: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  isRememberMe: boolean;
  createdAt: string;
  updatedAt: string;
  loggedInAt: string;
  loginCount: number;
  hasTrained: boolean;
  organisation: string;
  stepCount: number;
  payment: {
    userId: string;
    paymentStatus: string;
    isCanceledAtPeriodEnd: boolean;
    isYearlySubscription: boolean;
    isYearlyTrial: boolean;
  };
}

// Interface for signup error response
export interface SignupErrorResponse {
  errorCode: number;
  errorMessage?: string;
}

// Authentication related interfaces

// Interface for login credentials
export interface LoginCredentials {
  email: string;
  password: string;
  isRememberMe: boolean;
}

// Interface for login response
export interface LoginResponse {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  user: UserProfile;
}

// Interface for login error response
export interface LoginErrorResponse {
  errorCode: number;
  errorMessage?: string;
}

// Login error class
export class LoginError extends Error {
  public readonly errorCode: number;

  constructor(errorCode: number, message?: string) {
    super(message || `Login failed`);
    this.name = 'LoginError';
    this.errorCode = errorCode;
  }
}

// Interface for user profile
export interface UserProfile {
  id: string;
  sub: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  isRememberMe: boolean;
  createdAt: string;
  updatedAt: string;
  loggedInAt: string;
  loginCount: number;
  hasTrained: boolean;
  stepCount: number;
  // The store's name as signed up; chatbot-api signs the shopper's handoff email with it.
  organisation?: string;
  stepName?: string; // Comma-separated completed step names (e.g., "connect-store,support-email")
  fpRefId?: string; // FirstPromoter referral code bound to this user
  // True once the user holds any (non-canceled) credit subscription; the welcome
  // credits modal is shown only while this is false.
  welcomeCreditsClaimed?: boolean;
  // Active credit plan code (ai_starter / ai_growth / free / …), or null/undefined
  // on the legacy billing rail. Used for credit-aware plan gating.
  creditPlanCode?: string | null;
  // Plan code of the most-recent credit subscription regardless of status (incl.
  // canceled). Display-only: lets the billing page mark the cancelled tier correctly
  // (creditPlanCode is null after a trial cancel). NOT for gating.
  latestCreditPlanCode?: string | null;
  // True while in the no-card free trial. Grants product access even though
  // creditPlanCode is "free" (a trial is not a paid plan).
  inFreeTrial?: boolean;
  payment: PaymentInfo;
  installations: Installation[];
  productScan: ProductScan;
  productScanResult?: ProductScanResult;
  sync?: SyncInfo;
  // Additional properties for UI compatibility
  name?: string;
  role?: 'user' | 'admin';
  avatar?: string;
  permissions?: string[];
}

// Interface for payment information
export interface PaymentInfo {
  userId: string;
  paymentStatus: string;
  subscriptionStartsAt: string;
  subscriptionEndsAt: string;
  isCanceledAtPeriodEnd: boolean;
  isYearlySubscription: boolean;
  isYearlyTrial: boolean;
}

// Interface for installation
export interface Installation {
  id: string;
  userId: string;
  platform: string;
  url: string;
  status: string;
  /**
   * 挂件在已发布主题里启没启用：`'true'` / `'false'` / 缺字段（= 还没查到过）。
   *
   * ⚠️ **不要直接读它**，用 `@lib/widgetState` 的 `readWidgetState` /
   * `isWidgetLive` —— 那里带着**四条**必须一起遵守的规则（先过 uninstalled、
   * 只对 shopify 有意义、`'true'` 才算开着、**超过 72 小时没被巡检问过的值不算数**
   * —— 最后那条看 `switchStatusCheckedAt`，不是 `updatedAt`）。
   * 最后那条是第二、三轮评审的全部内容，别按这个列表在新调用点手写判据。
   *
   * ⚠️ 后端用 `NON_NULL` 序列化，所以「还没查到过」在线上是**字段缺失**，
   * 不是 `null`。判空要用 `== null` 或 optional chaining。
   */
  switchStatus?: string;
  /**
   * 上面那个答案**是什么时候问出来的**（ISO 8601，后端 `OffsetDateTime`）。
   *
   * ⚠️ 这不是 `updatedAt` 的别名。后端只有 `updateSwitchStatus` 会盖这个章，
   * `clearSwitchStatus*` 把它和值一起清成 null —— 它**只**在真的问过一次
   * 已发布主题时动。而 `updatedAt` 还会被访客流量、时区更新、连接状态变更、
   * 以及「200 但读不出结果」的那条刻意不写值的路径推着走（chatbot-api #2189）。
   * 拿 `updatedAt` 判新鲜度有个没有上限的洞，见 `@lib/widgetState` 的 `isFrozen`。
   *
   * ⚠️ 同样是 `NON_NULL` 序列化 → 「还没查到过」是**字段缺失**。
   * 这一列**刻意没有回填**，所以 chatbot-api #2202 部署后到巡检跑过之前，
   * 线上每一行都是缺失的。
   */
  switchStatusCheckedAt?: string;
  createdAt: string;
  updatedAt: string;
  hostUrl?: string;
}

// Interface for product scan
export interface ProductScan {
  id: string;
  userId: string;
  executionId: string;
  status: string;
  platform: string;
  createdAt: string;
  updatedAt: string;
}

// Interface for product scan result
export interface ProductScanResult {
  collectionIds: string[];
  productIds: string[];
  collectionCount: number;
  productCount: number;
}

// Interface for sync information
export interface SyncInfo {
  lastOperationTime: string;
  totalSyncCount: number;
  syncedCount: number;
  remainingSyncCount: number;
  lastPayload?: {
    shopifyUrl: string;
    scanLimit: number;
    productList: string[];
    collectionList: string[];
    disableProductList: string[];
    disableCollectionList: string[];
    selectAll: boolean;
  };
}

// Interface for forgot password code model
export interface ForgetPwdCodeModel {
  email: string;
  code: string;
  password: string;
}

// Interface for resend email response
export interface ResendEmailResponse {
  success?: boolean;
  errorCode?: string;
  errorMessage?: string;
}

// Interface for confirm email payload
export interface ConfirmEmailPayload {
  email: string;
  code: string;
}

// Interface for confirm email response
export interface ConfirmEmailResponse {
  user?: UserProfile;
  errorCode?: string;
  errorMessage?: string;
}

// Shopify integration DTO
export interface ShopifyDto {
  query: string;
}

// Integration activation payload
export interface IntegrationActivationPayload {
  id: string;
  email: string;
  shopify: ShopifyDto;
}

// Integration activation error response
export interface IntegrationActivationErrorResponse {
  errorCode: string;
  errorMessage: string;
}

export type IntegrationActivationResponse = UserProfile | IntegrationActivationErrorResponse;

// Google OAuth login types
export interface GoogleOAuthInitiateRequest {
  hmac: string;
  host: string;
  shop: string;
  state: string;
  code: string;
  timestamp: string;
}

export interface GoogleOAuthInitiateResponse {
  status: number;
  data: {
    authorization_url: string;
    state: string;
    context: {
      shop: string;
      hmac: string;
      code: string;
      host: string;
      state: string;
      email?: string;
      timestamp: string;
    };
  };
  message: string;
}

export interface GoogleOAuthCallbackResponse {
  gState: string;
  gCode: string;
  additionalParameters: {
    hmac: string;
    host: string;
    shop: string;
    timestamp: string;
    email?: string;
  };
}

// "Continue with Shopify" handoff (Onboarding v3): POST /auth/shopify trades the
// one-time code the ShopifyApp OAuth redirect hands us for a sign-in decision.
export type ShopifyHandoffAction =
  | 'signed_in'
  | 'already_signed_in'
  | 'bound'
  | 'confirm_required'
  | 'login_required'
  | 'register_required'
  | 'store_change_required';

export interface ShopifyHandoffResult {
  action: ShopifyHandoffAction;
  /** `signed_in` only — same shape as a normal login response. */
  auth?: LoginResponse;
  /** `signed_in` only — true when this handoff created a brand-new account. */
  accountCreated?: boolean;
  /** `login_required` only — never an email address, just which method to use. */
  loginHint?: 'email' | 'google';
  /** `register_required` (prefill) / `login_required` (display only). */
  email?: string;
  shop?: string;
  /** `confirm_required` only. */
  confirm?: 'bind' | 'switch';
  shopName?: string;
  /**
   * `confirm_required` only — a freshly-minted handoff code (the one used for this
   * call is consumed); lives 90s and must be sent back with `confirm: true`.
   */
  handoff?: string;
}
